import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../../App";
import { toast } from "sonner";
import { ImagePickerField } from "../../components/admin/ImagePickerField";
import { NewsManager } from "../../components/admin/NewsManager";
import { ComplianceManager } from "../../components/admin/ComplianceManager";

// === AUTH HELPER ===
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

// === KONSTANTEN ===
const FONTS = ["Barlow", "Manrope", "Arial", "Open Sans"];
const SIZES = ["text-sm", "text-base", "text-lg", "text-xl", "text-2xl", "text-3xl", "text-4xl", "text-5xl"];

// === KOMPONENTEN ===
function Card({ title, children, className = "" }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 ${className}`}>
      {title && <div className="px-6 py-4 border-b border-gray-100"><h3 className="font-semibold text-[#0A2A43]" style={{ fontFamily: "Barlow" }}>{title}</h3></div>}
      <div className="p-6">{children}</div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder = "", type = "text" }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0A2A43]/50" />
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder = "", rows = 3 }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0A2A43]/50 resize-none" />
    </div>
  );
}

function Btn({ children, onClick, variant = "primary", className = "", disabled = false, ...props }) {
  const base = "px-4 py-2 rounded-lg font-medium transition text-sm disabled:opacity-50";
  const variants = {
    primary: "bg-[#0A2A43] text-white hover:bg-[#0A2A43]/90",
    danger: "bg-[#D32F2F] text-white hover:bg-[#D32F2F]/90",
    secondary: "bg-gray-100 text-gray-700 hover:bg-gray-200",
  };
  return <button onClick={onClick} disabled={disabled} className={`${base} ${variants[variant]} ${className}`} {...props}>{children}</button>;
}

function StyleEditor({ label, color, setColor, size, setSize, font, setFont }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
      <p className="text-sm font-semibold text-[#0A2A43] mb-3">{label}</p>
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Farbe:</label>
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Größe:</label>
          <select value={size} onChange={(e) => setSize(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm">
            {SIZES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Schriftart:</label>
          <select value={font} onChange={(e) => setFont(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm">
            {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

// === SLIDER MANAGER ===
function SliderManager() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editSlide, setEditSlide] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newSlide, setNewSlide] = useState({ image_url: "", title: "", subtitle: "", order: 0, title_color: "#FFFFFF", title_size: "text-4xl", title_font: "Barlow", subtitle_color: "#FFFFFF", subtitle_size: "text-xl", subtitle_font: "Manrope" });

  useEffect(() => { loadSlides(); }, []);

  async function loadSlides() {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/slides`);
      // Sort by order
      const sorted = (res.data || []).sort((a, b) => (a.order || 0) - (b.order || 0));
      setSlides(sorted);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function addSlide() {
    if (!newSlide.title || !newSlide.image_url) return toast.error("Titel und Bild-URL erforderlich");
    try {
      await axios.post(`${API}/slides`, { ...newSlide, order: slides.length }, auth());
      toast.success("Slide hinzugefügt");
      setNewSlide({ image_url: "", title: "", subtitle: "", order: slides.length, title_color: "#FFFFFF", title_size: "text-4xl", title_font: "Barlow", subtitle_color: "#FFFFFF", subtitle_size: "text-xl", subtitle_font: "Manrope" });
      loadSlides();
    } catch { toast.error("Fehler beim Hinzufügen"); }
  }

  async function saveSlide(slide) {
    try {
      await axios.put(`${API}/slides/${slide.id}`, slide, auth());
      toast.success("Gespeichert");
      setEditSlide(null);
      loadSlides();
    } catch { toast.error("Fehler beim Speichern"); }
  }

  async function deleteSlide(id) {
    if (!window.confirm("Slide wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/slides/${id}`, auth());
      toast.success("Gelöscht");
      loadSlides();
    } catch { toast.error("Fehler beim Löschen"); }
  }

  // Move slide position
  async function moveSlide(index, direction) {
    if (saving) return;
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= slides.length) return;

    setSaving(true);
    try {
      // Create new order array
      const newSlides = [...slides];
      const [movedSlide] = newSlides.splice(index, 1);
      newSlides.splice(newIndex, 0, movedSlide);

      // Update all positions
      const updatePromises = newSlides.map((slide, i) => 
        axios.put(`${API}/slides/${slide.id}`, { ...slide, order: i }, auth())
      );
      await Promise.all(updatePromises);

      toast.success("Position geändert");
      loadSlides();
    } catch (e) {
      toast.error("Fehler beim Verschieben");
      console.error(e);
    }
    setSaving(false);
  }

  if (loading) return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2A43] mx-auto"></div></div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#0A2A43]" style={{ fontFamily: "Barlow" }}>Slider verwalten</h2>
      
      {/* Neuer Slide */}
      <Card title="Neuer Slide hinzufügen">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <ImagePickerField label="Bild *" value={newSlide.image_url} onChange={image_url => setNewSlide({ ...newSlide, image_url })} dataTestId="slide-create-image-picker" />
            <Input label="Titel *" value={newSlide.title} onChange={v => setNewSlide({ ...newSlide, title: v })} />
            <Input label="Untertitel" value={newSlide.subtitle} onChange={v => setNewSlide({ ...newSlide, subtitle: v })} />
            <StyleEditor label="Titel-Styling" color={newSlide.title_color} setColor={c => setNewSlide({ ...newSlide, title_color: c })} size={newSlide.title_size} setSize={s => setNewSlide({ ...newSlide, title_size: s })} font={newSlide.title_font} setFont={f => setNewSlide({ ...newSlide, title_font: f })} />
            <Btn variant="danger" onClick={addSlide}>+ Slide hinzufügen</Btn>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-2">Vorschau:</p>
            <div className="rounded-xl overflow-hidden h-48 relative" style={{ backgroundImage: newSlide.image_url ? `url(${newSlide.image_url})` : "linear-gradient(135deg, #0A2A43, #1a4a6e)", backgroundSize: "cover", backgroundPosition: "center" }}>
              <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-center p-4">
                <h4 className="font-bold text-white" style={{ color: newSlide.title_color, fontFamily: newSlide.title_font }}>{newSlide.title || "Titel"}</h4>
                <p className="text-white/80 text-sm mt-1">{newSlide.subtitle}</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Vorhandene Slides mit Reihenfolge */}
      <Card title={`Vorhandene Slides (${slides.length})`}>
        <p className="text-sm text-gray-500 mb-4">Verwenden Sie die Pfeile, um die Reihenfolge der Slides zu ändern.</p>
        <div className="space-y-4">
          {slides.map((s, index) => (
            <div key={s.id} className="bg-gray-50 rounded-lg overflow-hidden border flex flex-col md:flex-row">
              {/* Position Controls */}
              <div className="flex md:flex-col items-center justify-center gap-1 p-3 bg-[#0A2A43] md:w-16">
                <button 
                  onClick={() => moveSlide(index, -1)} 
                  disabled={index === 0 || saving}
                  className={`w-8 h-8 rounded flex items-center justify-center transition ${index === 0 ? 'bg-white/10 text-white/30 cursor-not-allowed' : 'bg-white/20 text-white hover:bg-white/30'}`}
                  title="Nach oben"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                </button>
                <span className="text-white font-bold text-lg w-8 h-8 flex items-center justify-center bg-[#D32F2F] rounded">
                  {index + 1}
                </span>
                <button 
                  onClick={() => moveSlide(index, 1)} 
                  disabled={index === slides.length - 1 || saving}
                  className={`w-8 h-8 rounded flex items-center justify-center transition ${index === slides.length - 1 ? 'bg-white/10 text-white/30 cursor-not-allowed' : 'bg-white/20 text-white hover:bg-white/30'}`}
                  title="Nach unten"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
              </div>

              {/* Bild-Vorschau */}
              <div className="w-full md:w-48 h-32 md:h-auto relative flex-shrink-0" style={{ backgroundImage: `url(${s.image_url})`, backgroundSize: "cover", backgroundPosition: "center" }}>
                <div className="absolute inset-0 bg-black/20"></div>
              </div>

              {/* Content */}
              <div className="flex-1 p-4">
                {editSlide?.id === s.id ? (
                  <div className="space-y-2">
                    <input value={editSlide.title} onChange={e => setEditSlide({ ...editSlide, title: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" placeholder="Titel" />
                    <input value={editSlide.subtitle} onChange={e => setEditSlide({ ...editSlide, subtitle: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" placeholder="Untertitel" />
                    <ImagePickerField label="Bild" value={editSlide.image_url} onChange={image_url => setEditSlide({ ...editSlide, image_url })} dataTestId={`slide-edit-image-picker-${s.id}`} />
                    <div className="flex gap-2">
                      <Btn onClick={() => saveSlide(editSlide)}>Speichern</Btn>
                      <Btn variant="secondary" onClick={() => setEditSlide(null)}>Abbrechen</Btn>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="bg-[#0A2A43] text-white text-xs px-2 py-0.5 rounded">Position {index + 1}</span>
                    </div>
                    <p className="font-semibold text-[#0A2A43]">{s.title}</p>
                    <p className="text-sm text-gray-500">{s.subtitle}</p>
                    <div className="flex gap-3 mt-3">
                      <button onClick={() => setEditSlide({ ...s })} className="text-[#0A2A43] text-sm hover:underline flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        Bearbeiten
                      </button>
                      <button onClick={() => deleteSlide(s.id)} className="text-red-500 text-sm hover:underline flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        Löschen
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
          {slides.length === 0 && <p className="text-gray-400 text-center py-8">Keine Slides vorhanden</p>}
        </div>
      </Card>
    </div>
  );
}

// === SERVICES MANAGER ===
function ServicesManager() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editService, setEditService] = useState(null);
  const [newService, setNewService] = useState({ 
    name: "", slug: "", short_description: "", full_description: "", image_url: "", order: 0, benefits: [],
    name_color: "#0A2A43", name_size: "text-xl", name_font: "Barlow",
    desc_color: "#4B5563", desc_size: "text-base", desc_font: "Manrope"
  });
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => { loadServices(); }, []);

  async function loadServices() {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/services`);
      setServices([...(res.data || [])].sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function addService() {
    if (!newService.name) return toast.error("Name erforderlich");
    try {
      const slug = newService.name.toLowerCase().replace(/\s+/g, "-").replace(/[äöüß]/g, c => ({ ä: "ae", ö: "oe", ü: "ue", ß: "ss" }[c] || c));
      await axios.post(`${API}/services`, { ...newService, slug, order: services.length }, auth());
      toast.success("Leistung hinzugefügt");
      setNewService({ 
        name: "", slug: "", short_description: "", full_description: "", image_url: "", order: 0, benefits: [],
        name_color: "#0A2A43", name_size: "text-xl", name_font: "Barlow",
        desc_color: "#4B5563", desc_size: "text-base", desc_font: "Manrope"
      });
      loadServices();
    } catch { toast.error("Fehler beim Hinzufügen"); }
  }

  async function saveService(service) {
    try {
      await axios.put(`${API}/services/${service.id}`, service, auth());
      toast.success("Gespeichert");
      setEditService(null);
      loadServices();
    } catch { toast.error("Fehler beim Speichern"); }
  }

  async function deleteService(id) {
    if (!window.confirm("Leistung wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/services/${id}`, auth());
      toast.success("Gelöscht");
      loadServices();
    } catch { toast.error("Fehler beim Löschen"); }
  }

  async function moveService(index, direction) {
    if (savingOrder) return;
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= services.length) return;
    setSavingOrder(true);
    try {
      const reordered = [...services];
      const [moved] = reordered.splice(index, 1);
      reordered.splice(newIndex, 0, moved);
      await Promise.all(reordered.map((service, order) => axios.put(`${API}/services/${service.id}`, { ...service, order }, auth())));
      setServices(reordered.map((service, order) => ({ ...service, order })));
      if (editService?.id) {
        const updatedEdit = reordered.find((service) => service.id === editService.id);
        if (updatedEdit) setEditService(updatedEdit);
      }
      toast.success("Reihenfolge gespeichert");
    } catch {
      toast.error("Fehler beim Verschieben");
      loadServices();
    }
    setSavingOrder(false);
  }

  if (loading) return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2A43] mx-auto"></div></div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#0A2A43]" style={{ fontFamily: "Barlow" }}>Leistungen verwalten</h2>
      
      {/* Neue Leistung */}
      <Card title="Neue Leistung hinzufügen">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <Input label="Name *" value={newService.name} onChange={v => setNewService({ ...newService, name: v })} placeholder="z.B. Rohbau" />
            <StyleEditor label="Name-Styling" color={newService.name_color} setColor={c => setNewService({ ...newService, name_color: c })} size={newService.name_size} setSize={s => setNewService({ ...newService, name_size: s })} font={newService.name_font} setFont={f => setNewService({ ...newService, name_font: f })} />
            <TextArea label="Kurzbeschreibung" value={newService.short_description} onChange={v => setNewService({ ...newService, short_description: v })} rows={2} />
            <StyleEditor label="Beschreibung-Styling" color={newService.desc_color} setColor={c => setNewService({ ...newService, desc_color: c })} size={newService.desc_size} setSize={s => setNewService({ ...newService, desc_size: s })} font={newService.desc_font} setFont={f => setNewService({ ...newService, desc_font: f })} />
            <TextArea label="Vollständige Beschreibung" value={newService.full_description} onChange={v => setNewService({ ...newService, full_description: v })} rows={4} />
            <ImagePickerField label="Bild" value={newService.image_url} onChange={image_url => setNewService({ ...newService, image_url })} dataTestId="service-create-image-picker" />
            <Btn variant="danger" onClick={addService}>+ Leistung hinzufügen</Btn>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-2">Vorschau:</p>
            <div className="bg-gray-50 rounded-xl p-4 border">
              {newService.image_url && <img src={newService.image_url} alt="" className="w-full h-32 object-cover rounded mb-3" />}
              <h4 className={`font-bold ${newService.name_size}`} style={{ color: newService.name_color, fontFamily: newService.name_font }}>{newService.name || "Name"}</h4>
              <p className={`mt-2 ${newService.desc_size}`} style={{ color: newService.desc_color, fontFamily: newService.desc_font }}>{newService.short_description || "Beschreibung..."}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Vorhandene Leistungen */}
      <Card title={`Vorhandene Leistungen (${services.length})`}>
        <p className="text-sm text-gray-500 mb-4">Pfeile speichern die neue Reihenfolge sofort.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {services.map((s, index) => (
            <div key={s.id} className="bg-gray-50 rounded-lg overflow-hidden border">
              {/* Bild */}
              {s.image_url && (
                <div className="h-32 relative" style={{ backgroundImage: `url(${s.image_url})`, backgroundSize: "cover", backgroundPosition: "center" }}>
                  <div className="absolute top-2 right-2 bg-[#0A2A43] text-white text-xs px-2 py-1 rounded">#{s.order + 1}</div>
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <span className="inline-flex bg-[#0A2A43] text-white text-xs px-2 py-1 rounded">Position {index + 1}</span>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => moveService(index, -1)} disabled={index === 0 || savingOrder} data-testid={`service-move-up-${s.id}`} className={`w-8 h-8 rounded flex items-center justify-center ${index === 0 ? "bg-gray-200 text-gray-400" : "bg-white border text-[#0A2A43] hover:bg-gray-100"}`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                    </button>
                    <button onClick={() => moveService(index, 1)} disabled={index === services.length - 1 || savingOrder} data-testid={`service-move-down-${s.id}`} className={`w-8 h-8 rounded flex items-center justify-center ${index === services.length - 1 ? "bg-gray-200 text-gray-400" : "bg-white border text-[#0A2A43] hover:bg-gray-100"}`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                  </div>
                </div>
                {editService?.id === s.id ? (
                  <div className="space-y-2">
                    <input value={editService.name} onChange={e => setEditService({ ...editService, name: e.target.value })} className="w-full border rounded px-2 py-1 text-sm font-semibold" />
                    <StyleEditor label="Name-Styling" color={editService.name_color || "#0A2A43"} setColor={c => setEditService({ ...editService, name_color: c })} size={editService.name_size || "text-xl"} setSize={s => setEditService({ ...editService, name_size: s })} font={editService.name_font || "Barlow"} setFont={f => setEditService({ ...editService, name_font: f })} />
                    <textarea value={editService.short_description} onChange={e => setEditService({ ...editService, short_description: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" rows={2} />
                    <StyleEditor label="Beschreibung-Styling" color={editService.desc_color || "#4B5563"} setColor={c => setEditService({ ...editService, desc_color: c })} size={editService.desc_size || "text-base"} setSize={s => setEditService({ ...editService, desc_size: s })} font={editService.desc_font || "Manrope"} setFont={f => setEditService({ ...editService, desc_font: f })} />
                    <textarea value={editService.full_description} onChange={e => setEditService({ ...editService, full_description: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" rows={3} />
                    <ImagePickerField label="Bild" value={editService.image_url} onChange={image_url => setEditService({ ...editService, image_url })} dataTestId={`service-edit-image-picker-${s.id}`} />
                    <div className="flex gap-2">
                      <Btn onClick={() => saveService(editService)}>Speichern</Btn>
                      <Btn variant="secondary" onClick={() => setEditService(null)}>Abbrechen</Btn>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className={`font-semibold ${s.name_size || 'text-base'}`} style={{ color: s.name_color || '#0A2A43', fontFamily: s.name_font || 'Barlow' }}>{s.name}</p>
                    <p className={`mt-1 line-clamp-2 ${s.desc_size || 'text-sm'}`} style={{ color: s.desc_color || '#6B7280', fontFamily: s.desc_font || 'Manrope' }}>{s.short_description}</p>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => setEditService({ ...s })} className="text-[#0A2A43] text-xs hover:underline">Bearbeiten</button>
                      <button onClick={() => deleteService(s.id)} className="text-red-500 text-xs hover:underline">Löschen</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
          {services.length === 0 && <p className="text-gray-400 col-span-2 text-center py-8">Keine Leistungen vorhanden</p>}
        </div>
      </Card>
    </div>
  );
}

// === CONTENT MANAGER ===
function ContentManager() {
  const [welcome, setWelcome] = useState({ 
    title: "", content: "",
    title_color: "#0A2A43", title_size: "text-2xl", title_font: "Barlow",
    content_color: "#4B5563", content_size: "text-base", content_font: "Manrope"
  });
  const [about, setAbout] = useState({ 
    title: "", content: "", image_url: "",
    title_color: "#0A2A43", title_size: "text-2xl", title_font: "Barlow",
    content_color: "#4B5563", content_size: "text-base", content_font: "Manrope"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadContent(); }, []);

  async function loadContent() {
    setLoading(true);
    try {
      const [welcomeRes, aboutRes] = await Promise.all([
        axios.get(`${API}/welcome`),
        axios.get(`${API}/about`),
      ]);
      setWelcome({ 
        title: "", content: "",
        title_color: "#0A2A43", title_size: "text-2xl", title_font: "Barlow",
        content_color: "#4B5563", content_size: "text-base", content_font: "Manrope",
        ...welcomeRes.data 
      });
      setAbout({ 
        title: "", content: "", image_url: "",
        title_color: "#0A2A43", title_size: "text-2xl", title_font: "Barlow",
        content_color: "#4B5563", content_size: "text-base", content_font: "Manrope",
        ...aboutRes.data 
      });
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function saveWelcome() {
    setSaving(true);
    try {
      await axios.put(`${API}/welcome`, welcome, auth());
      toast.success("Willkommen-Text gespeichert");
    } catch { toast.error("Fehler beim Speichern"); }
    setSaving(false);
  }

  async function saveAbout() {
    setSaving(true);
    try {
      await axios.put(`${API}/about`, about, auth());
      toast.success("Über-uns-Inhalt gespeichert");
    } catch { toast.error("Fehler beim Speichern"); }
    setSaving(false);
  }

  if (loading) return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2A43] mx-auto"></div></div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#0A2A43]" style={{ fontFamily: "Barlow" }}>Inhalte verwalten</h2>
      
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Willkommen */}
        <Card title="Willkommen (Startseite)">
          <Input label="Titel" value={welcome.title} onChange={v => setWelcome({ ...welcome, title: v })} />
          <StyleEditor label="Titel-Styling" color={welcome.title_color} setColor={c => setWelcome({ ...welcome, title_color: c })} size={welcome.title_size} setSize={s => setWelcome({ ...welcome, title_size: s })} font={welcome.title_font} setFont={f => setWelcome({ ...welcome, title_font: f })} />
          <TextArea label="Inhalt" value={welcome.content} onChange={v => setWelcome({ ...welcome, content: v })} rows={6} />
          <StyleEditor label="Inhalt-Styling" color={welcome.content_color} setColor={c => setWelcome({ ...welcome, content_color: c })} size={welcome.content_size} setSize={s => setWelcome({ ...welcome, content_size: s })} font={welcome.content_font} setFont={f => setWelcome({ ...welcome, content_font: f })} />
          <Btn onClick={saveWelcome} disabled={saving}>{saving ? "Speichern..." : "Speichern"}</Btn>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
            <p className="text-xs text-gray-400 mb-2">Vorschau:</p>
            <h4 className={`font-bold ${welcome.title_size}`} style={{ color: welcome.title_color, fontFamily: welcome.title_font }}>{welcome.title || "Titel"}</h4>
            <p className={`mt-2 whitespace-pre-wrap ${welcome.content_size}`} style={{ color: welcome.content_color, fontFamily: welcome.content_font }}>{welcome.content || "Inhalt..."}</p>
          </div>
        </Card>

        {/* Über uns */}
        <Card title="Über uns">
          <Input label="Titel" value={about.title} onChange={v => setAbout({ ...about, title: v })} />
          <StyleEditor label="Titel-Styling" color={about.title_color} setColor={c => setAbout({ ...about, title_color: c })} size={about.title_size} setSize={s => setAbout({ ...about, title_size: s })} font={about.title_font} setFont={f => setAbout({ ...about, title_font: f })} />
          <TextArea label="Inhalt" value={about.content} onChange={v => setAbout({ ...about, content: v })} rows={6} />
          <StyleEditor label="Inhalt-Styling" color={about.content_color} setColor={c => setAbout({ ...about, content_color: c })} size={about.content_size} setSize={s => setAbout({ ...about, content_size: s })} font={about.content_font} setFont={f => setAbout({ ...about, content_font: f })} />
          <Input label="Bild-URL" value={about.image_url} onChange={v => setAbout({ ...about, image_url: v })} placeholder="https://..." />
          <Btn onClick={saveAbout} disabled={saving}>{saving ? "Speichern..." : "Speichern"}</Btn>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
            <p className="text-xs text-gray-400 mb-2">Vorschau:</p>
            {about.image_url && <img src={about.image_url} alt="" className="w-full h-32 object-cover rounded mb-3" />}
            <h4 className={`font-bold ${about.title_size}`} style={{ color: about.title_color, fontFamily: about.title_font }}>{about.title || "Titel"}</h4>
            <p className={`mt-2 whitespace-pre-wrap ${about.content_size}`} style={{ color: about.content_color, fontFamily: about.content_font }}>{about.content || "Inhalt..."}</p>
          </div>
        </Card>
      </div>
    </div>
  );
}

// === PROJECTS MANAGER ===
function ProjectsManager() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProject, setEditProject] = useState(null);
  const [newProject, setNewProject] = useState({ 
    name: "", description: "", category: "", status: "aktiv", images: [],
    name_color: "#0A2A43", name_size: "text-xl", name_font: "Barlow",
    desc_color: "#4B5563", desc_size: "text-base", desc_font: "Manrope"
  });

  useEffect(() => { loadProjects(); }, []);

  async function loadProjects() {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function addProject() {
    if (!newProject.name) return toast.error("Name erforderlich");
    try {
      await axios.post(`${API}/projects`, newProject, auth());
      toast.success("Projekt hinzugefügt");
      setNewProject({ 
        name: "", description: "", category: "", status: "aktiv", images: [],
        name_color: "#0A2A43", name_size: "text-xl", name_font: "Barlow",
        desc_color: "#4B5563", desc_size: "text-base", desc_font: "Manrope"
      });
      loadProjects();
    } catch { toast.error("Fehler beim Hinzufügen"); }
  }

  async function saveProject(project) {
    try {
      await axios.put(`${API}/projects/${project.id}`, project, auth());
      toast.success("Gespeichert");
      setEditProject(null);
      loadProjects();
    } catch { toast.error("Fehler beim Speichern"); }
  }

  async function deleteProject(id) {
    if (!window.confirm("Projekt wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/projects/${id}`, auth());
      toast.success("Gelöscht");
      loadProjects();
    } catch { toast.error("Fehler beim Löschen"); }
  }

  if (loading) return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2A43] mx-auto"></div></div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#0A2A43]" style={{ fontFamily: "Barlow" }}>Projekte verwalten</h2>
      
      {/* Neues Projekt */}
      <Card title="Neues Projekt hinzufügen">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <Input label="Projektname *" value={newProject.name} onChange={v => setNewProject({ ...newProject, name: v })} />
            <StyleEditor label="Name-Styling" color={newProject.name_color} setColor={c => setNewProject({ ...newProject, name_color: c })} size={newProject.name_size} setSize={s => setNewProject({ ...newProject, name_size: s })} font={newProject.name_font} setFont={f => setNewProject({ ...newProject, name_font: f })} />
            <TextArea label="Beschreibung" value={newProject.description} onChange={v => setNewProject({ ...newProject, description: v })} rows={3} />
            <StyleEditor label="Beschreibung-Styling" color={newProject.desc_color} setColor={c => setNewProject({ ...newProject, desc_color: c })} size={newProject.desc_size} setSize={s => setNewProject({ ...newProject, desc_size: s })} font={newProject.desc_font} setFont={f => setNewProject({ ...newProject, desc_font: f })} />
            <Input label="Kategorie" value={newProject.category} onChange={v => setNewProject({ ...newProject, category: v })} placeholder="z.B. Rohbau, Kellerbau" />
            <ImagePickerField label="Bilder" values={newProject.images} onValuesChange={images => setNewProject({ ...newProject, images })} multiple dataTestId="project-create-image-picker" buttonLabel="Bild auswählen" />
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={newProject.status} onChange={e => setNewProject({ ...newProject, status: e.target.value })} className="w-full border rounded-lg px-3 py-2">
                <option value="aktiv">Aktiv</option>
                <option value="abgeschlossen">Abgeschlossen</option>
              </select>
            </div>
            <Btn variant="danger" onClick={addProject}>+ Projekt hinzufügen</Btn>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-2">Vorschau:</p>
            <div className="bg-gray-50 rounded-xl p-4 border">
              <div className="flex gap-2 mb-3 flex-wrap">
                {(newProject.images || []).slice(0, 4).map((img, i) => (
                  <img key={i} src={img} alt="" className="w-16 h-16 object-cover rounded" />
                ))}
              </div>
              <h4 className={`font-bold ${newProject.name_size}`} style={{ color: newProject.name_color, fontFamily: newProject.name_font }}>{newProject.name || "Projektname"}</h4>
              <p className={`mt-1 ${newProject.desc_size}`} style={{ color: newProject.desc_color, fontFamily: newProject.desc_font }}>{newProject.description || "Beschreibung..."}</p>
              <div className="flex gap-2 mt-2">
                {newProject.category && <span className="bg-[#0A2A43]/10 text-[#0A2A43] text-xs px-2 py-1 rounded">{newProject.category}</span>}
                <span className={`text-xs px-2 py-1 rounded ${newProject.status === "aktiv" ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>{newProject.status}</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Vorhandene Projekte */}
      <Card title={`Projekte (${projects.length})`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <div key={p.id} className="bg-gray-50 rounded-lg overflow-hidden border">
              {/* Bilder */}
              {p.images && p.images.length > 0 && (
                <div className="h-24 flex gap-1 overflow-hidden">
                  {p.images.slice(0, 3).map((img, i) => (
                    <div key={i} className="flex-1 h-full" style={{ backgroundImage: `url(${img})`, backgroundSize: "cover", backgroundPosition: "center" }}></div>
                  ))}
                </div>
              )}
              <div className="p-4">
                {editProject?.id === p.id ? (
                  <div className="space-y-2">
                    <input value={editProject.name} onChange={e => setEditProject({ ...editProject, name: e.target.value })} className="w-full border rounded px-2 py-1 text-sm font-semibold" />
                    <StyleEditor label="Name-Styling" color={editProject.name_color || "#0A2A43"} setColor={c => setEditProject({ ...editProject, name_color: c })} size={editProject.name_size || "text-xl"} setSize={s => setEditProject({ ...editProject, name_size: s })} font={editProject.name_font || "Barlow"} setFont={f => setEditProject({ ...editProject, name_font: f })} />
                    <textarea value={editProject.description} onChange={e => setEditProject({ ...editProject, description: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" rows={2} />
                    <StyleEditor label="Beschreibung-Styling" color={editProject.desc_color || "#4B5563"} setColor={c => setEditProject({ ...editProject, desc_color: c })} size={editProject.desc_size || "text-base"} setSize={s => setEditProject({ ...editProject, desc_size: s })} font={editProject.desc_font || "Manrope"} setFont={f => setEditProject({ ...editProject, desc_font: f })} />
                    <input value={editProject.category || ""} onChange={e => setEditProject({ ...editProject, category: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" placeholder="Kategorie" />
                    <select value={editProject.status || "aktiv"} onChange={e => setEditProject({ ...editProject, status: e.target.value })} className="w-full border rounded px-2 py-1 text-sm">
                      <option value="aktiv">Aktiv</option>
                      <option value="abgeschlossen">Abgeschlossen</option>
                    </select>
                    <ImagePickerField label="Bilder" values={editProject.images || []} onValuesChange={images => setEditProject({ ...editProject, images })} multiple dataTestId={`project-edit-image-picker-${p.id}`} buttonLabel="Bild auswählen" />
                    <div className="flex gap-2">
                      <Btn onClick={() => saveProject(editProject)}>Speichern</Btn>
                      <Btn variant="secondary" onClick={() => setEditProject(null)}>Abbrechen</Btn>
                    </div>
                  </div>
                ) : (
                  <>
                    <h5 className={`font-semibold ${p.name_size || 'text-base'}`} style={{ color: p.name_color || '#0A2A43', fontFamily: p.name_font || 'Barlow' }}>{p.name}</h5>
                    <p className={`mt-1 line-clamp-2 ${p.desc_size || 'text-sm'}`} style={{ color: p.desc_color || '#6B7280', fontFamily: p.desc_font || 'Manrope' }}>{p.description}</p>
                    <div className="flex gap-2 mt-2">
                      {p.category && <span className="bg-[#0A2A43]/10 text-[#0A2A43] text-xs px-2 py-1 rounded">{p.category}</span>}
                      <span className={`text-xs px-2 py-1 rounded ${p.status === "aktiv" ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>{p.status}</span>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => setEditProject({ ...p })} className="text-[#0A2A43] text-xs hover:underline">Bearbeiten</button>
                      <button onClick={() => deleteProject(p.id)} className="text-red-500 text-xs hover:underline">Löschen</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
          {projects.length === 0 && <p className="text-gray-400 col-span-3 text-center py-8">Keine Projekte vorhanden</p>}
        </div>
      </Card>
    </div>
  );
}

// === CONTACT SUBMISSIONS MANAGER ===
function SubmissionsManager() {
  const DEFAULT_NOTIFICATION_EMAIL = "info@gashi-betonbau.de";
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState("anfragen"); // "anfragen" oder "einstellungen"
  
  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    email_enabled: true,
    notification_email: "info@gashi-betonbau.de",
    email_subject_prefix: "[Kontaktanfrage]",
    include_phone: true,
    include_message: true,
    resend_configured: false,
    missing_configuration: []
  });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => { 
    loadSubmissions(); 
    loadNotificationSettings();
  }, []);

  async function loadSubmissions() {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contact-submissions`, auth());
      setSubmissions(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function loadNotificationSettings() {
    try {
      const res = await axios.get(`${API}/notification-settings`, auth());
      setNotificationSettings((current) => ({ ...current, ...(res.data || {}) }));
    } catch (e) { console.error(e); }
  }

  async function saveNotificationSettings() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (notificationSettings.email_enabled && !emailRegex.test(notificationSettings.notification_email)) {
      toast.error("Bitte geben Sie eine gültige Empfänger-E-Mail-Adresse ein");
      return;
    }
    
    setSavingSettings(true);
    try {
      const res = await axios.put(`${API}/notification-settings`, notificationSettings, auth());
      if (res.data?.settings) {
        setNotificationSettings((current) => ({ ...current, ...res.data.settings }));
      }
      toast.success("Einstellungen gespeichert");
    } catch (e) { 
      toast.error("Fehler beim Speichern"); 
    }
    setSavingSettings(false);
  }

  async function markAsRead(id) {
    try {
      await axios.put(`${API}/contact-submissions/${id}/read`, {}, auth());
      loadSubmissions();
    } catch (e) { console.error(e); }
  }

  async function deleteSubmission(id) {
    if (!window.confirm("Anfrage wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/contact-submissions/${id}`, auth());
      toast.success("Gelöscht");
      setSelected(null);
      loadSubmissions();
    } catch { toast.error("Fehler beim Löschen"); }
  }

  function exportCSV() {
    const rows = [["Name", "E-Mail", "Telefon", "Betreff", "Nachricht", "Datum", "Gelesen"]];
    submissions.forEach(s => {
      rows.push([s.name, s.email, s.phone || "", s.subject, s.message, new Date(s.created_at).toLocaleDateString("de-DE"), s.is_read ? "Ja" : "Nein"]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "kontaktanfragen.csv";
    a.click();
  }

  if (loading) return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2A43] mx-auto"></div></div>;

  const unreadCount = submissions.filter(s => !s.is_read).length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#0A2A43]" style={{ fontFamily: "Barlow" }}>Kontaktanfragen</h2>
          <p className="text-sm text-gray-500">{submissions.length} Anfragen gesamt, {unreadCount} ungelesen</p>
        </div>
        <div className="flex gap-2">
          <Btn onClick={exportCSV} variant="secondary">📥 CSV Export</Btn>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        <button 
          onClick={() => setActiveTab("anfragen")} 
          className={`px-4 py-2 rounded-t-lg font-medium transition ${activeTab === "anfragen" ? "bg-[#0A2A43] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
        >
          📧 Anfragen ({submissions.length})
        </button>
        <button 
          onClick={() => setActiveTab("einstellungen")}
          data-testid="notification-settings-tab"
          className={`px-4 py-2 rounded-t-lg font-medium transition ${activeTab === "einstellungen" ? "bg-[#0A2A43] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
        >
          E-Mail-Weiterleitung
        </button>
      </div>

      {activeTab === "anfragen" ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Liste */}
          <Card title="Alle Anfragen" className="lg:col-span-2">
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {submissions.map((s) => (
                <div key={s.id} onClick={() => { setSelected(s); if (!s.is_read) markAsRead(s.id); }} className={`p-4 rounded-lg cursor-pointer border transition ${selected?.id === s.id ? "bg-[#0A2A43]/5 border-[#0A2A43]" : "bg-gray-50 hover:bg-gray-100 border-transparent"}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {!s.is_read && <span className="w-2 h-2 bg-[#D32F2F] rounded-full"></span>}
                        <p className="font-semibold text-[#0A2A43]">{s.name}</p>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{s.subject}</p>
                      <p className="text-xs text-gray-400 mt-1 line-clamp-1">{s.message}</p>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap ml-2">{new Date(s.created_at).toLocaleDateString("de-DE")}</span>
                  </div>
                </div>
              ))}
              {submissions.length === 0 && <p className="text-gray-400 text-center py-12">Keine Anfragen vorhanden</p>}
            </div>
          </Card>

          {/* Details */}
          <Card title="Details">
            {selected ? (
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500">Name</label>
                  <p className="font-semibold text-[#0A2A43]">{selected.name}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">E-Mail</label>
                  <p className="text-[#0A2A43]"><a href={`mailto:${selected.email}`} className="hover:underline">{selected.email}</a></p>
                </div>
                {selected.phone && (
                  <div>
                    <label className="text-xs text-gray-500">Telefon</label>
                    <p><a href={`tel:${selected.phone}`} className="hover:underline">{selected.phone}</a></p>
                  </div>
                )}
                <div>
                  <label className="text-xs text-gray-500">Betreff</label>
                  <p>{selected.subject}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Nachricht</label>
                  <p className="bg-gray-50 p-3 rounded-lg text-sm whitespace-pre-wrap">{selected.message}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Datum</label>
                  <p>{new Date(selected.created_at).toLocaleString("de-DE")}</p>
                </div>
                <div className="flex gap-2 pt-4">
                  <a href={`mailto:${selected.email}?subject=Re: ${selected.subject}`} className="flex-1 bg-[#0A2A43] text-white text-center py-2 rounded-lg text-sm hover:bg-[#0A2A43]/90">Antworten</a>
                  <Btn variant="danger" onClick={() => deleteSubmission(selected.id)}>Löschen</Btn>
                </div>
              </div>
            ) : (
              <p className="text-gray-400 text-center py-12">Anfrage auswählen</p>
            )}
          </Card>
        </div>
      ) : (
        /* Notification Settings Tab */
        <Card title="E-Mail-Weiterleitung konfigurieren">
          <div className="max-w-2xl">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-semibold text-blue-800">Automatische E-Mail-Benachrichtigung</p>
                  <p className="text-sm text-blue-600 mt-1">
                    Wenn aktiviert, erhalten Sie bei jeder neuen Kontaktanfrage automatisch eine E-Mail mit allen Anfrage-Details.
                  </p>
                </div>
              </div>
            </div>

            {!notificationSettings.resend_configured && (
              <div data-testid="notification-config-warning" className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="font-semibold text-amber-900">Resend ist noch nicht vollständig konfiguriert</p>
                <p className="text-sm text-amber-800 mt-1">
                  Kontaktanfragen werden weiterhin gespeichert, aber aktuell nicht per E-Mail weitergeleitet.
                </p>
                <p className="text-xs text-amber-700 mt-2">
                  Fehlende Konfiguration: {(notificationSettings.missing_configuration || []).join(", ") || "RESEND_API_KEY, SENDER_EMAIL"}
                </p>
              </div>
            )}

            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-6">
              <div>
                <p className="font-semibold text-[#0A2A43]">E-Mail-Weiterleitung aktivieren</p>
                <p className="text-sm text-gray-500">Neue Anfragen werden automatisch per E-Mail weitergeleitet</p>
              </div>
              <button 
                onClick={() => setNotificationSettings({ ...notificationSettings, email_enabled: !notificationSettings.email_enabled })}
                data-testid="notification-email-toggle"
                className={`w-14 h-7 rounded-full transition-colors relative ${notificationSettings.email_enabled ? "bg-green-500" : "bg-gray-300"}`}
              >
                <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${notificationSettings.email_enabled ? "left-8" : "left-1"}`}></span>
              </button>
            </div>

            {notificationSettings.email_enabled && (
              <div className="space-y-6">
                {/* Email Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Empfänger E-Mail-Adresse *
                  </label>
                  <input 
                    type="email"
                    data-testid="notification-recipient-email-input"
                    value={DEFAULT_NOTIFICATION_EMAIL}
                    readOnly
                    disabled
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 bg-gray-100 text-gray-600 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">Alle E-Mails werden ab jetzt an die Empfänger E-Mail-Adresse <strong>info@gashi-betonbau.de</strong> weitergeleitet.</p>
                </div>

                {/* Subject Prefix */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Betreff-Präfix
                  </label>
                  <input 
                    type="text"
                    data-testid="notification-subject-prefix-input"
                    value={notificationSettings.email_subject_prefix} 
                    onChange={e => setNotificationSettings({ ...notificationSettings, email_subject_prefix: e.target.value })}
                    placeholder="[Kontaktanfrage]"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0A2A43]/50"
                  />
                  <p className="text-xs text-gray-500 mt-1">Wird dem E-Mail-Betreff vorangestellt (z.B. "[Kontaktanfrage] Anfrage Rohbau")</p>
                </div>

                {/* Options */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">E-Mail-Inhalt</p>
                  
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.include_phone}
                      onChange={e => setNotificationSettings({ ...notificationSettings, include_phone: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-[#0A2A43] focus:ring-[#0A2A43]"
                    />
                    <span className="text-gray-700">Telefonnummer einbeziehen</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.include_message}
                      onChange={e => setNotificationSettings({ ...notificationSettings, include_message: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-[#0A2A43] focus:ring-[#0A2A43]"
                    />
                    <span className="text-gray-700">Nachrichtentext einbeziehen</span>
                  </label>
                </div>

                {/* Preview */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2 border-b">
                    <p className="text-sm font-medium text-gray-600">E-Mail Vorschau</p>
                  </div>
                  <div className="p-4 bg-white">
                    <p className="text-sm text-gray-500 mb-2">
                      <strong>An:</strong> {notificationSettings.notification_email || "---"}
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      <strong>Betreff:</strong> {notificationSettings.email_subject_prefix} Anfrage Rohbau
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4 text-sm">
                      <p><strong>Name:</strong> Max Mustermann</p>
                      <p><strong>E-Mail:</strong> max@example.de</p>
                      {notificationSettings.include_phone && <p><strong>Telefon:</strong> 0123 456789</p>}
                      <p><strong>Betreff:</strong> Anfrage Rohbau</p>
                      {notificationSettings.include_message && (
                        <p className="mt-2"><strong>Nachricht:</strong> Ich interessiere mich für Ihre Leistungen...</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="mt-6 pt-6 border-t">
              <Btn onClick={saveNotificationSettings} disabled={savingSettings} className="!px-8" data-testid="notification-settings-save-button">
                {savingSettings ? "Speichern..." : "Einstellungen speichern"}
              </Btn>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// === IMAGE MANAGER ===
function ImageManager() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedImage, setSelectedImage] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const CATEGORIES = [
    { id: "all", label: "Alle Bilder" },
    { id: "slider", label: "Slider" },
    { id: "services", label: "Leistungen" },
    { id: "projects", label: "Projekte" },
    { id: "general", label: "Allgemein" },
  ];

  useEffect(() => { loadImages(); }, [selectedCategory]);

  async function loadImages() {
    setLoading(true);
    try {
      const category = selectedCategory === "all" ? "" : selectedCategory;
      const url = category ? `${API}/images?category=${category}` : `${API}/images`;
      const res = await axios.get(url, auth());
      setImages(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function uploadFile(file) {
    if (!file) return;
    
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Nur JPG, PNG, GIF, WebP und SVG erlaubt");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Maximale Dateigröße: 5MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", selectedCategory === "all" ? "general" : selectedCategory);
      
      const res = await axios.post(`${API}/images/upload`, formData, {
        ...auth(),
        headers: { ...auth().headers, "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Bild hochgeladen");
      loadImages();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Hochladen");
    }
    setUploading(false);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  }

  function handleDrag(e) {
    e.preventDefault();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }

  async function deleteImage(id) {
    if (!window.confirm("Bild wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/images/${id}`, auth());
      toast.success("Bild gelöscht");
      setSelectedImage(null);
      loadImages();
    } catch { toast.error("Fehler beim Löschen"); }
  }

  async function updateCategory(id, category) {
    try {
      await axios.put(`${API}/images/${id}`, { category }, auth());
      toast.success("Kategorie aktualisiert");
      loadImages();
    } catch { toast.error("Fehler beim Aktualisieren"); }
  }

  function copyUrl(image) {
    const url = `${API}/images/${image.id}/data`;
    navigator.clipboard.writeText(url);
    toast.success("URL kopiert!");
  }

  function formatSize(bytes) {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }

  if (loading && images.length === 0) return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2A43] mx-auto"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#0A2A43]" style={{ fontFamily: "Barlow" }}>Bilderverwaltung</h2>
          <p className="text-sm text-gray-500">{images.length} Bilder</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map(c => (
            <button key={c.id} onClick={() => setSelectedCategory(c.id)} className={`px-3 py-1.5 rounded-lg text-sm transition ${selectedCategory === c.id ? "bg-[#0A2A43] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Upload Area */}
      <Card title="Bild hochladen">
        <div 
          onDrop={handleDrop} 
          onDragEnter={handleDrag} 
          onDragOver={handleDrag} 
          onDragLeave={handleDrag}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition ${dragActive ? "border-[#D32F2F] bg-red-50" : "border-gray-300 hover:border-[#0A2A43]"}`}
        >
          {uploading ? (
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0A2A43] mb-3"></div>
              <p className="text-gray-500">Wird hochgeladen...</p>
            </div>
          ) : (
            <>
              <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-600 mb-2">Bild hierher ziehen oder</p>
              <label className="cursor-pointer">
                <span className="bg-[#D32F2F] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#D32F2F]/90 transition">Datei auswählen</span>
                <input type="file" className="hidden" accept="image/*" onChange={e => uploadFile(e.target.files[0])} />
              </label>
              <p className="text-xs text-gray-400 mt-3">JPG, PNG, GIF, WebP, SVG • Max. 5MB</p>
            </>
          )}
        </div>
      </Card>

      {/* Image Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card title="Bilder" className="lg:col-span-2">
          {loading ? (
            <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2A43] mx-auto"></div></div>
          ) : images.length === 0 ? (
            <p className="text-gray-400 text-center py-12">Keine Bilder in dieser Kategorie</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 max-h-[500px] overflow-y-auto">
              {images.map(img => (
                <div key={img.id} onClick={() => setSelectedImage(img)} className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition ${selectedImage?.id === img.id ? "border-[#0A2A43]" : "border-transparent hover:border-gray-300"}`}>
                  <div className="aspect-square bg-gray-100">
                    <img src={`${API}/images/${img.id}/data`} alt={img.original_name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1">
                    <p className="text-white text-xs truncate">{img.original_name}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Image Details */}
        <Card title="Details">
          {selectedImage ? (
            <div className="space-y-4">
              <img src={`${API}/images/${selectedImage.id}/data`} alt={selectedImage.original_name} className="w-full rounded-lg" />
              <div>
                <label className="text-xs text-gray-500">Dateiname</label>
                <p className="font-semibold text-[#0A2A43] text-sm break-all">{selectedImage.original_name}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500">Größe</label>
                  <p className="text-sm">{formatSize(selectedImage.size)}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Typ</label>
                  <p className="text-sm">{selectedImage.content_type}</p>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500">Kategorie</label>
                <select value={selectedImage.category} onChange={e => updateCategory(selectedImage.id, e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm mt-1">
                  <option value="general">Allgemein</option>
                  <option value="slider">Slider</option>
                  <option value="services">Leistungen</option>
                  <option value="projects">Projekte</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Hochgeladen</label>
                <p className="text-sm">{new Date(selectedImage.created_at).toLocaleString("de-DE")}</p>
              </div>
              <div className="flex gap-2 pt-2">
                <Btn onClick={() => copyUrl(selectedImage)} className="flex-1">URL kopieren</Btn>
                <Btn variant="danger" onClick={() => deleteImage(selectedImage.id)}>Löschen</Btn>
              </div>
            </div>
          ) : (
            <p className="text-gray-400 text-center py-12">Bild auswählen</p>
          )}
        </Card>
      </div>
    </div>
  );
}

// === USER MANAGER ===
function UserManager() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState({ email: "", password: "" });
  const [editUser, setEditUser] = useState(null);

  useEffect(() => { loadUsers(); }, []);

  async function loadUsers() {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users`, auth());
      setUsers(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function addUser() {
    if (!newUser.email || !newUser.password) return toast.error("E-Mail und Passwort erforderlich");
    try {
      await axios.post(`${API}/users`, newUser, auth());
      toast.success("Benutzer hinzugefügt");
      setNewUser({ email: "", password: "" });
      loadUsers();
    } catch (e) { 
      toast.error(e.response?.data?.detail || "Fehler beim Hinzufügen"); 
    }
  }

  async function updateUser() {
    if (!editUser.email) return toast.error("E-Mail erforderlich");
    try {
      await axios.put(`${API}/users/${editUser.id}`, editUser, auth());
      toast.success("Benutzer aktualisiert");
      setEditUser(null);
      loadUsers();
    } catch (e) { 
      toast.error(e.response?.data?.detail || "Fehler beim Aktualisieren"); 
    }
  }

  async function deleteUser(id) {
    if (!window.confirm("Benutzer wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/users/${id}`, auth());
      toast.success("Benutzer gelöscht");
      loadUsers();
    } catch (e) { 
      toast.error(e.response?.data?.detail || "Fehler beim Löschen"); 
    }
  }

  if (loading) return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2A43] mx-auto"></div></div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#0A2A43]" style={{ fontFamily: "Barlow" }}>Benutzerverwaltung</h2>
      
      {/* Neuer Benutzer */}
      <Card title="Neuen Administrator hinzufügen">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <Input label="E-Mail *" type="email" value={newUser.email} onChange={v => setNewUser({ ...newUser, email: v })} placeholder="admin@example.de" />
          <Input label="Passwort *" type="password" value={newUser.password} onChange={v => setNewUser({ ...newUser, password: v })} placeholder="Mindestens 6 Zeichen" />
          <Btn variant="danger" onClick={addUser} className="h-10">+ Benutzer hinzufügen</Btn>
        </div>
      </Card>

      {/* Benutzerliste */}
      <Card title={`Administratoren (${users.length})`}>
        <div className="space-y-3">
          {users.map((u) => (
            <div key={u.id} className="bg-gray-50 rounded-lg p-4 border flex items-center justify-between flex-wrap gap-4">
              {editUser?.id === u.id ? (
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                  <input type="email" value={editUser.email} onChange={e => setEditUser({ ...editUser, email: e.target.value })} className="border rounded px-3 py-2" placeholder="E-Mail" />
                  <input type="password" value={editUser.password || ""} onChange={e => setEditUser({ ...editUser, password: e.target.value })} className="border rounded px-3 py-2" placeholder="Neues Passwort (optional)" />
                  <div className="flex gap-2">
                    <Btn onClick={updateUser}>Speichern</Btn>
                    <Btn variant="secondary" onClick={() => setEditUser(null)}>Abbrechen</Btn>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0A2A43] rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-bold">{u.email.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-[#0A2A43]">{u.email}</p>
                      <p className="text-xs text-gray-500">Erstellt: {u.created_at ? new Date(u.created_at).toLocaleDateString("de-DE") : "—"}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditUser({ ...u, password: "" })} className="text-[#0A2A43] text-sm hover:underline">Bearbeiten</button>
                    <button onClick={() => deleteUser(u.id)} className="text-red-500 text-sm hover:underline">Löschen</button>
                  </div>
                </>
              )}
            </div>
          ))}
          {users.length === 0 && <p className="text-gray-400 text-center py-8">Keine Benutzer vorhanden</p>}
        </div>
      </Card>
    </div>
  );
}

// === SETTINGS MANAGER ===
function SettingsManager() {
  const [settings, setSettings] = useState({});
  const [footer, setFooter] = useState({});
  const [seo, setSeo] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  async function loadSettings() {
    setLoading(true);
    try {
      const [settingsRes, footerRes, seoRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/footer-settings`),
        axios.get(`${API}/seo-settings`),
      ]);
      setSettings(settingsRes.data || {});
      setFooter(footerRes.data || {});
      setSeo(seoRes.data || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function saveSettings() {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/settings`, settings, auth()),
        axios.put(`${API}/footer-settings`, footer, auth()),
        axios.put(`${API}/seo-settings`, seo, auth()),
      ]);
      toast.success("Alle Einstellungen gespeichert");
    } catch { toast.error("Fehler beim Speichern"); }
    setSaving(false);
  }

  if (loading) return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2A43] mx-auto"></div></div>;

  // Social Media Icons for Preview
  const socialIcons = [
    { key: "facebook_url", icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>, name: "Facebook" },
    { key: "instagram_url", icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>, name: "Instagram" },
    { key: "linkedin_url", icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>, name: "LinkedIn" },
    { key: "youtube_url", icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>, name: "YouTube" },
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#0A2A43]" style={{ fontFamily: "Barlow" }}>Einstellungen</h2>
      
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Firmeninfos */}
        <Card title="Firmeninformationen">
          <Input label="Firmenname" value={settings.company_name || ""} onChange={v => setSettings({ ...settings, company_name: v })} />
          <Input label="Untertitel" value={settings.company_subtitle || ""} onChange={v => setSettings({ ...settings, company_subtitle: v })} />
          <Input label="Geschäftsführer" value={settings.ceo_name || ""} onChange={v => setSettings({ ...settings, ceo_name: v })} />
          <Input label="Logo-URL" value={settings.logo_url || ""} onChange={v => setSettings({ ...settings, logo_url: v })} placeholder="https://..." />
          {settings.logo_url && <img src={settings.logo_url} alt="Logo" className="h-12 mt-2 rounded" />}
        </Card>

        {/* Social Links mit Live-Vorschau */}
        <Card title="Social Media">
          <Input label="Facebook" value={settings.facebook_url || ""} onChange={v => setSettings({ ...settings, facebook_url: v })} placeholder="https://facebook.com/..." />
          <Input label="Instagram" value={settings.instagram_url || ""} onChange={v => setSettings({ ...settings, instagram_url: v })} placeholder="https://instagram.com/..." />
          <Input label="LinkedIn" value={settings.linkedin_url || ""} onChange={v => setSettings({ ...settings, linkedin_url: v })} placeholder="https://linkedin.com/..." />
          <Input label="YouTube" value={settings.youtube_url || ""} onChange={v => setSettings({ ...settings, youtube_url: v })} placeholder="https://youtube.com/..." />
          
          {/* Live-Vorschau */}
          <div className="mt-4 p-4 bg-[#0A2A43] rounded-lg">
            <p className="text-xs text-white/60 mb-3">Live-Vorschau (Footer-Icons):</p>
            <div className="flex gap-3">
              {socialIcons.map((item) => (
                <div
                  key={item.key}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    settings[item.key] 
                      ? "bg-white/20 text-white cursor-pointer hover:bg-[#D32F2F]" 
                      : "bg-white/5 text-white/30 cursor-not-allowed"
                  }`}
                  title={settings[item.key] ? `${item.name}: ${settings[item.key]}` : `${item.name}: Nicht konfiguriert`}
                >
                  {item.icon}
                </div>
              ))}
            </div>
            <p className="text-xs text-white/40 mt-2">
              {socialIcons.filter(i => settings[i.key]).length} von 4 Social-Media-Links aktiv
            </p>
          </div>
        </Card>

        {/* Footer */}
        <Card title="Footer-Einstellungen">
          <Input label="Adresse" value={footer.address || ""} onChange={v => setFooter({ ...footer, address: v })} />
          <Input label="Telefon" value={footer.phone || ""} onChange={v => setFooter({ ...footer, phone: v })} />
          <Input label="Mobil" value={footer.mobile || ""} onChange={v => setFooter({ ...footer, mobile: v })} />
          <Input label="Fax" value={footer.fax || ""} onChange={v => setFooter({ ...footer, fax: v })} />
          <Input label="E-Mail" value={footer.email || ""} onChange={v => setFooter({ ...footer, email: v })} />
        </Card>

        {/* SEO */}
        <Card title="SEO-Einstellungen">
          <Input label="Meta-Titel" value={seo.meta_title || ""} onChange={v => setSeo({ ...seo, meta_title: v })} />
          <TextArea label="Meta-Beschreibung" value={seo.meta_description || ""} onChange={v => setSeo({ ...seo, meta_description: v })} rows={3} />
          <Input label="Keywords" value={seo.keywords || ""} onChange={v => setSeo({ ...seo, keywords: v })} placeholder="Betonbau, Rohbau, ..." />
          <Input label="Social Share Bild" value={seo.og_image || ""} onChange={v => setSeo({ ...seo, og_image: v })} placeholder="https://..." />
          {seo.og_image && <img src={seo.og_image} alt="OG Image" className="w-full h-32 object-cover mt-2 rounded" />}
        </Card>
      </div>

      <Btn onClick={saveSettings} disabled={saving} className="!px-8 !py-3">
        {saving ? "Speichern..." : "Alle Einstellungen speichern"}
      </Btn>
    </div>
  );
}

// === DASHBOARD ===
function Dashboard({ onNavigate }) {
  const [stats, setStats] = useState({ total: 0, new: 0, services: 0, slides: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadStats() {
      try {
        const [statsRes, servicesRes, slidesRes] = await Promise.all([
          axios.get(`${API}/dashboard-stats`, auth()),
          axios.get(`${API}/services`),
          axios.get(`${API}/slides`),
        ]);
        setStats({
          total: statsRes.data?.total || 0,
          new: statsRes.data?.new || 0,
          services: servicesRes.data?.length || 0,
          slides: slidesRes.data?.length || 0,
        });
      } catch (e) { console.error(e); }
      setLoading(false);
    }
    loadStats();
  }, []);

  if (loading) return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2A43] mx-auto"></div></div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#0A2A43]" style={{ fontFamily: "Barlow" }}>Dashboard</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#D32F2F] rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A2A43]">{stats.total}</p>
              <p className="text-sm text-gray-500">Anfragen gesamt</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A2A43]">{stats.new}</p>
              <p className="text-sm text-gray-500">Neue Anfragen</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#0A2A43] rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A2A43]">{stats.services}</p>
              <p className="text-sm text-gray-500">Leistungen</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A2A43]">{stats.slides}</p>
              <p className="text-sm text-gray-500">Slides</p>
            </div>
          </div>
        </div>
      </div>

      <Card title="Schnellzugriff">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            type="button"
            onClick={() => window.open("/", "_blank", "noopener,noreferrer")}
            data-testid="dashboard-quick-website"
            className="bg-gray-50 rounded-lg p-4 text-center hover:bg-gray-100 hover:-translate-y-0.5 transition cursor-pointer border border-transparent hover:border-[#0A2A43]/10"
          >
            <svg className="w-8 h-8 mx-auto text-[#0A2A43] mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
            <p className="text-sm font-medium text-[#0A2A43]">Website ansehen</p>
          </button>
          <button
            type="button"
            onClick={() => onNavigate("anfragen")}
            data-testid="dashboard-quick-submissions"
            className="bg-gray-50 rounded-lg p-4 text-center hover:bg-gray-100 hover:-translate-y-0.5 transition cursor-pointer border border-transparent hover:border-[#0A2A43]/10"
          >
            <svg className="w-8 h-8 mx-auto text-[#0A2A43] mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            <p className="text-sm font-medium text-[#0A2A43]">Anfragen prüfen</p>
          </button>
          <button
            type="button"
            onClick={() => onNavigate("bilder")}
            data-testid="dashboard-quick-images"
            className="bg-gray-50 rounded-lg p-4 text-center hover:bg-gray-100 hover:-translate-y-0.5 transition cursor-pointer border border-transparent hover:border-[#0A2A43]/10"
          >
            <svg className="w-8 h-8 mx-auto text-[#0A2A43] mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            <p className="text-sm font-medium text-[#0A2A43]">Bilder verwalten</p>
          </button>
          <button
            type="button"
            onClick={() => onNavigate("einstellungen")}
            data-testid="dashboard-quick-settings"
            className="bg-gray-50 rounded-lg p-4 text-center hover:bg-gray-100 hover:-translate-y-0.5 transition cursor-pointer border border-transparent hover:border-[#0A2A43]/10"
          >
            <svg className="w-8 h-8 mx-auto text-[#0A2A43] mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            <p className="text-sm font-medium text-[#0A2A43]">Einstellungen</p>
          </button>
        </div>
      </Card>
    </div>
  );
}

// === HAUPTKOMPONENTE ===
export default function AdminDashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState("dashboard");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Auth Check
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/admin/login");
    }
  }, [user, authLoading, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2A43]"></div>
      </div>
    );
  }

  if (!user) return null;

  const NAV = [
    { id: "dashboard", label: "Dashboard", icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg> },
    { id: "slider", label: "Slider verwalten", icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> },
    { id: "leistungen", label: "Leistungen", icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg> },
    { id: "inhalte", label: "Inhalte", icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> },
    { id: "projekte", label: "Projekte", icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg> },
    { id: "news", label: "News", icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 5H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2V7a2 2 0 00-2-2z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 9h10M7 13h6" /></svg> },
    { id: "bilder", label: "Bilderverwaltung", icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> },
    { id: "anfragen", label: "Anfragen", icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg> },
    { id: "compliance", label: "DSGVO & Sicherheit", icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0-3.866 3.582-7 8-7 0 0 1 9-8 17C3 13 4 4 4 4c4.418 0 8 3.134 8 7z" /></svg> },
    { id: "benutzer", label: "Benutzer", icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg> },
    { id: "einstellungen", label: "Einstellungen", icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg> },
  ];

  const renderPage = () => {
    switch (page) {
      case "dashboard": return <Dashboard onNavigate={setPage} />;
      case "slider": return <SliderManager />;
      case "leistungen": return <ServicesManager />;
      case "inhalte": return <ContentManager />;
      case "projekte": return <ProjectsManager />;
      case "news": return <NewsManager />;
      case "bilder": return <ImageManager />;
      case "anfragen": return <SubmissionsManager />;
      case "compliance": return <ComplianceManager />;
      case "benutzer": return <UserManager />;
      case "einstellungen": return <SettingsManager />;
      default: return <Dashboard />;
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-100">
      {/* Mobile Menu Button */}
      <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden fixed top-4 left-4 z-50 bg-[#0A2A43] text-white p-2 rounded-lg shadow-lg" data-testid="mobile-menu-toggle">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {mobileMenuOpen ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
        </svg>
      </button>

      {/* Sidebar */}
      <aside className={`w-64 bg-[#0A2A43] text-white fixed h-full flex flex-col z-40 transition-transform duration-300 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} data-testid="admin-sidebar">
        <div className="p-6 border-b border-white/10">
          <img 
            src="https://customer-assets.emergentagent.com/job_betonbau-cms/artifacts/ybiq5sq0_logo.png" 
            alt="Gashi Bau GmbH" 
            className="h-10 mb-2"
          />
          <p className="text-sm text-white/60" style={{ fontFamily: "Manrope" }}>Admin-Panel</p>
        </div>
        
        <nav className="flex-1 py-4 overflow-y-auto">
          {NAV.map(n => (
            <button key={n.id} onClick={() => { setPage(n.id); setMobileMenuOpen(false); }} data-testid={`nav-${n.id}`} className={`w-full flex items-center gap-3 px-6 py-3 text-left text-sm transition ${page === n.id ? "bg-white/10 border-l-4 border-[#D32F2F]" : "hover:bg-white/5 border-l-4 border-transparent"}`}>
              {n.icon}
              <span>{n.label}</span>
            </button>
          ))}
        </nav>
        
        <div className="p-4 border-t border-white/10 space-y-3">
          <div className="flex items-center gap-3 px-2">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <span className="text-xs font-bold">{user?.email?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate">{user?.email}</p>
            </div>
          </div>
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-2 text-sm text-white/60 hover:text-white transition">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
            Website ansehen
          </a>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="logout-btn" className="flex items-center gap-2 px-2 text-sm text-red-400 hover:text-red-300 transition">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
            Abmelden
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileMenuOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setMobileMenuOpen(false)}></div>}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 p-4 lg:p-8 pt-16 lg:pt-8">
        {renderPage()}
      </main>
    </div>
  );
}
